import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, CreditCard, Receipt, LogOut, ChevronRight, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchProfile();
    }
  }, [user, authLoading, navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const navItems = [
    { path: '/account', icon: User, label: 'Dashboard', exact: true },
    { path: '/account/profile', icon: Settings, label: 'Traveler Profile' },
    { path: '/account/payments', icon: CreditCard, label: 'Payment Methods' },
    { path: '/account/bookings', icon: Receipt, label: 'My Bookings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Check if we're on a sub-route
  const isSubRoute = location.pathname !== '/account';

  return (
    <div className="py-8" data-testid="customer-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-maroon">
            Welcome, {profile?.full_name || user?.name || 'Traveler'}!
          </h1>
          <p className="text-maroon/70 mt-2">Manage your profile, bookings, and payment methods</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="card p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-maroon text-white'
                      : 'text-maroon hover:bg-sand/50'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  <ChevronRight size={16} className="ml-auto" />
                </Link>
              ))}
              
              <hr className="my-4 border-maroon/10" />
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-sm text-maroon hover:bg-red-50 hover:text-red-600 transition-colors w-full"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
                <span className="font-medium">Sign Out</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {isSubRoute ? (
              <Outlet context={{ profile, setProfile, fetchProfile }} />
            ) : (
              <DashboardHome profile={profile} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = ({ profile }) => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletter, setNewsletter] = useState(!!user?.newsletter_subscribed);
  const [savingNewsletter, setSavingNewsletter] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    setNewsletter(!!user?.newsletter_subscribed);
  }, [user?.newsletter_subscribed]);

  const toggleNewsletter = async (next) => {
    setSavingNewsletter(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customer/newsletter`, { subscribed: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewsletter(next);
      toast.success(next ? 'Subscribed to newsletter' : 'Unsubscribed');
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setSavingNewsletter(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const profileCompletion = () => {
    if (!profile) return 0;
    const fields = [
      profile.full_name,
      profile.phone,
      profile.date_of_birth,
      profile.experience_types?.length > 0,
      profile.hotel_budget,
      profile.emergency_contact_name
    ];
    const completed = fields.filter(Boolean).length;
    return Math.round((completed / fields.length) * 100);
  };

  const recentBookings = bookings.slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card bg-sand/30">
          <p className="text-sm text-maroon/60 mb-1">Profile Completion</p>
          <p className="text-3xl font-bold text-maroon">{profileCompletion()}%</p>
          {profileCompletion() < 100 && (
            <Link to="/account/profile" className="text-sm text-terracotta hover:underline mt-2 inline-block">
              Complete your profile →
            </Link>
          )}
        </div>
        <div className="card bg-sand/30">
          <p className="text-sm text-maroon/60 mb-1">Total Bookings</p>
          <p className="text-3xl font-bold text-maroon">{bookings.length}</p>
        </div>
        <div className="card bg-sand/30">
          <p className="text-sm text-maroon/60 mb-1">Confirmed Trips</p>
          <p className="text-3xl font-bold text-terracotta">
            {bookings.filter(b => b.booking_status === 'confirmed').length}
          </p>
        </div>
      </div>

      {/* Newsletter preference */}
      <div className="card flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-heading font-bold text-maroon">Monthly Newsletter</p>
          <p className="text-sm text-maroon/60">Travel inspiration & exclusive offers — once a month, no spam.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-maroon">
          <input
            type="checkbox"
            checked={newsletter}
            disabled={savingNewsletter}
            onChange={(e) => toggleNewsletter(e.target.checked)}
            data-testid="dashboard-newsletter-toggle"
          />
          {newsletter ? 'Subscribed' : 'Not subscribed'}
        </label>
      </div>

      {/* Recent Bookings */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-heading font-bold text-maroon">Recent Bookings</h2>
          <Link to="/account/bookings" className="text-sm text-terracotta hover:underline">
            View all →
          </Link>
        </div>
        
        {loading ? (
          <p className="text-maroon/60">Loading...</p>
        ) : recentBookings.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-maroon/60 mb-4">No bookings yet</p>
            <Link to="/partners" className="btn-primary inline-block">
              Browse Packages
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentBookings.map((booking) => (
              <div 
                key={booking.id} 
                className="flex items-center justify-between p-4 bg-sand/20 rounded-sm"
              >
                <div>
                  <p className="font-semibold text-maroon">{booking.package_title}</p>
                  <p className="text-sm text-maroon/60">
                    {booking.order_number || booking.id.slice(0, 8).toUpperCase()} • {booking.selected_trip_date || booking.preferred_dates}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  booking.booking_status === 'confirmed' 
                    ? 'bg-green-100 text-green-700' 
                    : booking.payment_status === 'paid'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {booking.booking_status === 'confirmed' ? 'Confirmed' : 
                   booking.payment_status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/partners" className="card hover:border-terracotta transition-colors group">
          <h3 className="font-heading font-bold text-maroon group-hover:text-terracotta">
            Browse New Adventures
          </h3>
          <p className="text-sm text-maroon/60 mt-1">Discover our latest travel packages</p>
        </Link>
        <Link to="/consultation" className="card hover:border-terracotta transition-colors group">
          <h3 className="font-heading font-bold text-maroon group-hover:text-terracotta">
            Plan Custom Trip
          </h3>
          <p className="text-sm text-maroon/60 mt-1">Schedule a free consultation</p>
        </Link>
      </div>
    </div>
  );
};

export default CustomerDashboard;
